/**
 * Раздел «Строительство» страницы «Аналитика» (US-23, задача DP-436) —
 * новый состав виджетов: плашка «Строительство» из четырёх плиток (объём
 * строительных работ + ИФО, ввод жилья + ИФО, количество квартир,
 * введено объектов нежилого назначения) и два блока — «Ввод объектов
 * нежилого назначения» и «Жилые дома в аварийном состоянии».
 *
 * Состав US-04.8 (плашка ИФО/прироста, виджеты объёмов и «Проектно-сметная
 * документация» с реестром ПСД) этим US **заменён**, а не дополнен
 * (FR-1.1): виджет ПСД снят и в раздел «Проекты» не переносится.
 *
 * Весь подраздел грузится **одной операцией** `/analytics/construction`:
 * разбиение на три (`construction/plashka`, `construction/nonresidential`,
 * `construction/emergency-housing`) было предложением US-24 FR-1.1 и бэком
 * не реализовано — раздельные пути отвечают 404. Отчётная подпись у
 * плашки и у каждого блока при этом остаётся своей, из своей части
 * ответа: объём работ и ввод жилья публикуются ежемесячно 18 числа, ввод
 * объектов нежилого назначения — ежеквартально, аварийное жильё — раз в
 * год, и общей отчётной подписи у страницы быть не может (FR-5.4).
 *
 * Отчётной подписи у плашки на экране нет (решение пользователя):
 * накопленный с начала года период (FR-2.6) приходит в ответе и служит
 * разбору, но подписью не выводится — раздел озаглавлен заголовком
 * секции. Подписи блоков ниже остаются своими (FR-5.1).
 *
 * Величины выводятся в единицах источника (FR-6.1): экран не приводит
 * объём к миллиардам, а площадь — к тысячам кв. м, а печатает значение и
 * объявленную ответом единицу. ИФО приходит готовым, экраном не
 * рассчитывается и сверху не ограничивается (FR-6.4, FR-6.5).
 *
 * Страница доступна на всех уровнях КАТО (FR-1.4); на уровнях, куда
 * статистика БНС не доходит, показатели наполняются ручным вводом, а
 * ненужный виджет выключается супер-администратором (FR-1.4.1).
 */
import {
  Building,
  Building2,
  Home,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ConstructionEmergencyHousing } from './ConstructionEmergencyHousing';
import { ConstructionNonresidential } from './ConstructionNonresidential';
import { fmt1, useStatBlock, useStatText } from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { WidgetErrorBoundary } from '../../../components/common/WidgetErrorBoundary';
import {
  StatCard,
  type StatCardFootnote,
} from '../../../components/widgets/StatCard';
import { MODULES } from '../../../consts/modules';
import { useKato } from '../../../contexts/KatoContext';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { getAnalyticsConstruction } from '../../../requests/analytics.request';
import type {
  ConstructionAnalyticsResponse,
  StatIfo,
  StatValue,
} from '../../../types/analytics';

interface TileVM {
  key: string;
  /** Знак показателя слева от значения (макет). */
  icon: LucideIcon;
  title: string;
  titleHint?: string;
  /** null — данных нет: плитка остаётся с подписью «нет данных». */
  value: string | null;
  unit: string | null;
  footnotes: StatCardFootnote[];
}

interface ConstructionSectionProps {
  /** Режим витрины «Все» (US-04.1): в сводной витрине плашек нет —
   * тема показана своими виджетами (ввод объектов и аварийное жильё),
   * а плашка «Строительство» остаётся в подразделе. */
  summary?: boolean;
}

export const ConstructionSection = ({
  summary = false,
}: ConstructionSectionProps = {}) => {
  const { t } = useTranslation();
  const { selectedKato } = useKato();
  const text = useStatText();

  // Видимость каждого виджета задаёт супер-администратор (US-09 FR-1):
  // выключенный виджет не отображается вовсе и места не занимает
  // (FR-1.3, FR-7.7), состоянием «нет данных» он не подменяется.
  const showPlashka = useModulePresent(MODULES.analytics.constructionPlashka);
  const showNonresidential = useModulePresent(
    MODULES.analytics.constructionNonresidential,
  );
  const showEmergency = useModulePresent(
    MODULES.analytics.constructionEmergencyHousing,
  );

  // Подраздел приходит одним ответом: плашка и блоки не запрашиваются
  // порознь — раздельных операций у бэка нет. Запрос уходит, пока на
  // экране есть хоть один виджет раздела; выключены все — не грузим.
  const construction = useStatBlock<ConstructionAnalyticsResponse>(
    getAnalyticsConstruction,
    (!summary && showPlashka) || showNonresidential || showEmergency,
  );
  const plashka = {
    data: construction.data?.plashka ?? null,
    loading: construction.loading,
  };
  const nonresidential = {
    data: construction.data?.nonresidential ?? null,
    loading: construction.loading,
  };
  const emergency = {
    data: construction.data?.emergency_housing ?? null,
    loading: construction.loading,
  };

  /**
   * Подпись ИФО плитки: по макету «ИФО» стоит приглушённой подписью, а
   * само значение — зелёным при положительной динамике и красным при
   * отрицательной (FR-6.6). База сравнения остаётся приглушённым
   * хвостом: она поясняет период, а не показывает динамику.
   */
  const ifoFootnote = (ifo: StatIfo | null): StatCardFootnote[] => {
    if (ifo === null || ifo.value === null || ifo.state !== 'value') return [];
    const grew = ifo.value >= 100;
    const good = ifo.direction === 'up_is_bad' ? !grew : grew;
    return [
      {
        label: t('analytics.stats.ifoShort'),
        // ИФО печатается как есть, в процентах (100,7 %, а не +0,7 %) и
        // без ограничения сверху: 391,3 % и 897,4 % — фактические
        // значения выпусков по районам (FR-6.5, FR-6.6).
        text: `${fmt1(ifo.value)}%`,
        suffix: ifo.base_label ?? undefined,
        bold: ifo.direction === null,
        success: ifo.direction !== null && good,
        danger: ifo.direction !== null && !good,
      },
    ];
  };

  /**
   * Плитка строится всегда — плашка состоит из четырёх фиксированных
   * плиток (замечание пользователя, как в «Сельском хозяйстве» после
   * DP-426 и в «Социальной сфере» после DP-429). Показателя нет —
   * остаётся название и серая подпись «нет данных» (режим `value: null`
   * у `StatCard`): пропавшая плитка читается как «показателя не
   * существует», а не как «данных пока не пришло», и ряд перестраивается
   * от того, что выпуск ещё не вышел. Постановка US-23 FR-2.7
   * предписывает обратное (плитку без величины не выводить) —
   * расхождение осознанное и подлежит отражению в US.
   */
  const tile = (
    key: string,
    icon: LucideIcon,
    title: string,
    value: StatValue | null,
    footnotes: StatCardFootnote[],
    titleHint?: string | null,
  ): TileVM => {
    if (
      value === null ||
      value.state === 'no_data' ||
      value.state === 'not_applicable'
    ) {
      return {
        key,
        icon,
        title,
        titleHint: titleHint ?? undefined,
        value: null,
        unit: null,
        footnotes: [],
      };
    }
    const shown = text(value, fmt1);
    return {
      key,
      icon,
      title,
      titleHint: titleHint ?? undefined,
      value: shown.text,
      unit: shown.known ? value.unit : null,
      footnotes: shown.known ? footnotes : [],
    };
  };

  const data = plashka.data;

  /**
   * «по N видам объектов» (FR-2.5): N считается по фактически выведенным
   * строкам блока, а не берётся константой «12 видов» из DP-384. Пока
   * блок не загружен (или выключен), берётся число видов из ответа
   * плашки (US-24 FR-6.5).
   */
  const kindsCount =
    showNonresidential &&
    !nonresidential.loading &&
    nonresidential.data !== null
      ? nonresidential.data.rows.length
      : (data?.nonresidential?.kinds_count ?? null);

  const tiles: TileVM[] = [
    tile(
      'volume',
      Building2,
      t('analytics.construction.plashka.volume'),
      data?.volume?.value ?? null,
      ifoFootnote(data?.volume?.ifo ?? null),
    ),
    tile(
      'housing',
      Home,
      t('analytics.construction.plashka.housing'),
      data?.housing?.value ?? null,
      ifoFootnote(data?.housing?.ifo ?? null),
    ),
    // Третья плитка носит наименование показателя источника «Количество
    // квартир, единиц» (FR-2.4); заголовок «Введено жилых домов» из
    // прототипа описывал другую величину и не используется.
    tile(
      'apartments',
      Building,
      t('analytics.construction.plashka.apartments'),
      data?.apartments ?? null,
      [{ text: t('analytics.construction.plashka.apartmentsNote') }],
      t('analytics.construction.plashka.apartmentsHint'),
    ),
    // Количество объектов — из итоговой строки квартального выпуска, а не
    // сумма строк блока: часть видов закрыта как конфиденциальная
    // (US-24 FR-6.4).
    tile(
      'nonresidential',
      Warehouse,
      t('analytics.construction.plashka.nonresidential'),
      data?.nonresidential?.count ?? null,
      kindsCount !== null
        ? [
            {
              text: t('analytics.construction.plashka.nonresidentialKinds', {
                count: kindsCount,
              }),
            },
          ]
        : [],
    ),
  ];

  return (
    <section className="space-y-4" data-testid="analytics-construction">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.construction.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Building2
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Плашка — только в тематическом подразделе: в сводной витрине
          «Все» плашек нет, темы показаны виджетами. */}
      {!summary && showPlashka && (
        <WidgetErrorBoundary
          key={`construction-plashka-${selectedKato ?? 'none'}`}
        >
          {plashka.loading ? (
            <BlockLoader />
          ) : (
            <div data-testid="construction-plashka">
              {/* Ряд из четырёх плиток (макет): все четыре в строку на
                  десктопе, по две на планшете. Сетка, а не flex-wrap с
                  min-w: одной минимальной шириной нельзя получить и
                  четыре плитки в 924 px на 1280, и две в 672 px на 768 —
                  четыре по 224 px требуют минимума меньше трети ширины
                  планшета. Плиток всегда четыре: без данных плитка
                  остаётся на месте с названием и подписью «нет данных»,
                  и ряд не перестраивается от того, что выпуск ещё не
                  вышел. */}
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                {tiles.map((card) => (
                  <div key={card.key} className="min-w-0">
                    <StatCard
                      testId={`analytics-card-construction-${card.key}`}
                      icon={card.icon}
                      title={card.title}
                      titleHint={card.titleHint}
                      value={card.value}
                      unit={card.unit}
                      footnotes={card.footnotes}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </WidgetErrorBoundary>
      )}

      {/* Порядок блоков фиксирован: ввод объектов, затем аварийное жильё
          (FR-1.3). Это же — ключевые виджеты темы в сводной витрине
          «Все» (плашка туда не идёт). По макету блоки стоят рядом: таблица занимает две трети ширины,
          аварийное жильё — треть. Две колонки — только от xl: на 1024
          под контент остаётся ~668 px, и таблица в двух третях от них
          уже не читается. Выключенный блок не оставляет пустой колонки —
          соседний занимает первую ячейку сетки (FR-1.3). */}
      {(showNonresidential || showEmergency) && (
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-3">
          {showNonresidential && (
            <WidgetErrorBoundary
              key={`construction-nonres-${selectedKato ?? 'none'}`}
            >
              <div className="h-full xl:col-span-2">
                <ConstructionNonresidential
                  data={nonresidential.data}
                  loading={nonresidential.loading}
                />
              </div>
            </WidgetErrorBoundary>
          )}

          {showEmergency && (
            <WidgetErrorBoundary
              key={`construction-emergency-${selectedKato ?? 'none'}`}
            >
              <ConstructionEmergencyHousing
                data={emergency.data}
                loading={emergency.loading}
              />
            </WidgetErrorBoundary>
          )}
        </div>
      )}
    </section>
  );
};
